import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { Notification, AIAlert } from '@/types';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.store';
import { Order } from '@/features/orders/types/order.type';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  onlineUsers: number;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const { user, tenant, isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (isAuthenticated && user && tenant) {
      // Initialize socket connection
      const socketInstance = io(import.meta.env.VITE_API_URL || 'http://localhost:3000', {
        auth: {
          token: localStorage.getItem('vldl_token'),
          userId: user.id,
          userType: 'vendor',
          tenantId: tenant.id,
        },
        transports: ['websocket', 'polling'],
      });

      // Connection event handlers
      socketInstance.on('connect', () => {
        console.log('Connected to server');
        setIsConnected(true);

        // Join tenant-specific room
        socketInstance.emit('join-tenant-room', tenant.id);

        toast.success('Connected to real-time updates');
      });

      socketInstance.on('disconnect', (reason) => {
        console.log('Disconnected from server:', reason);
        setIsConnected(false);

        if (reason === 'io server disconnect') {
          // Server initiated disconnect, try to reconnect
          socketInstance.connect();
        }
      });

      socketInstance.on('connect_error', (error) => {
        console.error('Connection error:', error);
        setIsConnected(false);
        toast.error('Connection error. Retrying...');
      });

      // Business event handlers
      socketInstance.on('new-order', (order: Order) => {
        console.log('New order received:', order);
        toast.success(`New order #${order.id.slice(-6)} received!`, {
          duration: 6000,
          icon: '🔔',
        });

        // Play notification sound (optional)
        try {
          const audio = new Audio('/notification.mp3');
          audio.play().catch(() => console.log('Could not play notification sound'));
        } catch {
          // Ignore audio errors
        }
      });

      socketInstance.on('order-update', (update: { orderId: string; status: Order['order_status']; timestamp: string }) => {
        console.log('Order update:', update);

        const statusMessages = {
          confirmed: 'Order confirmed by customer',
          cancelled: 'Order cancelled',
          picked_up: 'Order picked up by rider',
          delivered: 'Order delivered successfully',
        };

        const message = statusMessages[update.status as keyof typeof statusMessages];
        if (message) {
          toast.success(`Order #${update.orderId.slice(-6)}: ${message}`);
        }
      });

      socketInstance.on('ai-alert', (alert: AIAlert) => {
        console.log('AI Alert received:', alert);

        const alertStyles = {
          low: { icon: 'ℹ️', duration: 4000 },
          medium: { icon: '⚠️', duration: 6000 },
          high: { icon: '🚨', duration: 8000 },
          critical: { icon: '🔥', duration: 10000 },
        };

        const style = alertStyles[alert.severity];

        toast(alert.message, {
          icon: style.icon,
          duration: style.duration,
          style: {
            background: alert.severity === 'critical' ? '#ef4444' :
              alert.severity === 'high' ? '#f59e0b' : '#2D6734',
            color: '#fff',
          },
        });
      });

      socketInstance.on('metrics-update', (metrics) => {
        console.log('Metrics updated:', metrics);
        // This could trigger a refetch of dashboard data
        // We'll handle this in the dashboard components
      });

      socketInstance.on('notification', (notification: Notification) => {
        console.log('New notification:', notification);

        if (notification.priority === 'high' || notification.priority === 'critical') {
          toast(notification.message, {
            icon: notification.priority === 'critical' ? '🚨' : '⚠️',
            duration: 6000,
          });
        }
      });

      socketInstance.on('online-users', (count: number) => {
        setOnlineUsers(count);
      });

      // Error handling
      socketInstance.on('error', (error: any) => {
        console.error('Socket error:', error);
        toast.error('Real-time connection error');
      });

      setSocket(socketInstance);

      // Cleanup on unmount or auth change
      return () => {
        console.log('Cleaning up socket connection');
        socketInstance.disconnect();
        setSocket(null);
        setIsConnected(false);
      };
    }
  }, [isAuthenticated, user, tenant]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [socket]);

  const value: SocketContextType = {
    socket,
    isConnected,
    onlineUsers,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

// Custom hooks for specific socket events
export const useOrderUpdates = () => {
  const { socket } = useSocket();

  const subscribeToOrderUpdates = (callback: (order: Order) => void) => {
    if (socket) {
      socket.on('new-order', callback);
      return () => socket.off('new-order', callback);
    }
    return () => { };
  };

  const subscribeToOrderStatusUpdates = (callback: (update: any) => void) => {
    if (socket) {
      socket.on('order-update', callback);
      return () => socket.off('order-update', callback);
    }
    return () => { };
  };

  return {
    subscribeToOrderUpdates,
    subscribeToOrderStatusUpdates,
  };
};

export const useAIAlerts = () => {
  const { socket } = useSocket();

  const subscribeToAIAlerts = (callback: (alert: AIAlert) => void) => {
    if (socket) {
      socket.on('ai-alert', callback);
      return () => socket.off('ai-alert', callback);
    }
    return () => { };
  };

  return {
    subscribeToAIAlerts,
  };
};